import { format, parseISO } from 'date-fns';
import { zonedTimeToUtc, utcToZonedTime } from 'date-fns-tz';

class TimezoneService {
  private userTimezone: string = 'UTC';

  setUserTimezone(timezone: string) {
    this.userTimezone = timezone;
    localStorage.setItem('userTimezone', timezone);
  }

  getUserTimezone(): string {
    const stored = localStorage.getItem('userTimezone');
    return this.userTimezone !== 'UTC' ? this.userTimezone : (stored || 'UTC');
  }

  /**
   * Convert a UTC time string (HH:mm) to user's local time
   * @param utcTime - Time in HH:mm format (UTC)
   * @param referenceDate - Optional reference date for DST calculations
   */
  utcTimeToLocal(utcTime: string, referenceDate?: Date): string {
    if (!utcTime) return '';
    const [hours, minutes] = utcTime.split(':').map(Number);
    const ref = referenceDate || new Date();
    
    // Create a UTC date with the given time
    const utcDate = new Date(Date.UTC(
      ref.getFullYear(),
      ref.getMonth(),
      ref.getDate(),
      hours,
      minutes
    ));
    
    // Convert to user's timezone
    const zonedDate = utcToZonedTime(utcDate, this.getUserTimezone());
    return format(zonedDate, 'HH:mm');
  }

  /**
   * Convert a local time string (HH:mm) to UTC
   * @param localTime - Time in HH:mm format (user's timezone)
   * @param referenceDate - Optional reference date for DST calculations
   */
  localTimeToUtc(localTime: string, referenceDate?: Date): string {
    if (!localTime) return '';
    const ref = referenceDate || new Date();
    
    // Create a date in user's timezone
    // We use a specific date to handle DST correctly
    const dateStr = format(ref, 'yyyy-MM-dd');
    const localDateStr = `${dateStr}T${localTime.padStart(5, '0')}:00`;
    
    // Convert to UTC
    const utcDate = zonedTimeToUtc(localDateStr, this.getUserTimezone());
    return format(utcDate, 'HH:mm');
  }

  /**
   * Convert a UTC datetime to user's local datetime string
   */
  formatDateTimeLocal(utcDateString: string, formatStr: string = 'yyyy-MM-dd HH:mm:ss'): string {
    if (!utcDateString) return '';
    const utcDate = parseISO(utcDateString);
    const zonedDate = utcToZonedTime(utcDate, this.getUserTimezone());
    return format(zonedDate, formatStr);
  }

  /**
   * Get the UTC offset string for display (e.g., "+01:00")
   */
  getUtcOffsetString(): string {
    const now = new Date();
    const zonedDate = utcToZonedTime(now, this.getUserTimezone());
    return format(zonedDate, 'xxx');
  }

  /**
   * Get display name for timezone
   */
  getTimezoneDisplayName(): string {
    const tz = this.getUserTimezone();
    try {
      return new Intl.DateTimeFormat('en', {
        timeZone: tz,
        timeZoneName: 'long'
      }).formatToParts().find(p => p.type === 'timeZoneName')?.value || tz;
    } catch {
      return tz;
    }
  }
}

export const timezoneService = new TimezoneService();
export default timezoneService;
